import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import Button from '../../components/common/Button';
import Toast from '../../components/common/Toast';
import { useToast } from '../../hooks/useToast';
import engineerService from '../../services/engineerService';
import { getChecklistProgress, saveChecklistProgress } from '../../data/mockData';

const ChecklistView = () => {
  const { user } = useAuth();
  const [checklistTypes, setChecklistTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [checklistItems, setChecklistItems] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState([]);
  const { toasts, removeToast, success, error } = useToast();

  useEffect(() => {
    loadChecklistTypes();
  }, []);

  useEffect(() => {
    if (selectedType) {
      loadChecklistItems(selectedType);
    }
  }, [selectedType]);

  const loadChecklistTypes = async () => {
    setLoading(true);
    const result = await engineerService.getChecklistTypes();
    if (result.success) {
      setChecklistTypes(result.data);
      if (result.data.length > 0) {
        setSelectedType(result.data[0]);
      }
    }
    setLoading(false);
  };

  const loadChecklistItems = async (type) => {
    const result = await engineerService.getChecklistItems(type);
    if (result.success) {
      setChecklistItems(result.data);
      // Load saved progress
      const progress = getChecklistProgress(type);
      setCheckedItems(progress);
    }
  };

  const toggleItem = (item) => {
    const newCheckedItems = {
      ...checkedItems,
      [item]: !checkedItems[item]
    };
    setCheckedItems(newCheckedItems);
    saveChecklistProgress(selectedType, newCheckedItems);
  };

  const calculateCompletion = () => {
    if (checklistItems.length === 0) return 0;
    const completed = checklistItems.filter(item => checkedItems[item]).length;
    return Math.round((completed / checklistItems.length) * 100);
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    setPhotos(prev => [...prev, ...files]);
    success(`${files.length} photo(s) uploaded!`);
  };

  const handleSubmitChecklist = async () => {
    if (calculateCompletion() !== 100) {
      error('Please complete all checklist items before submitting');
      return;
    }

    const checklistData = {
      engineer_id: user.id,
      checklist_type: selectedType,
      items: checklistItems.map(item => ({
        item: item,
        checked: checkedItems[item] || false
      })),
      photos: photos,
      submitted_at: new Date().toISOString()
    };

    const result = await engineerService.submitChecklist(checklistData);
    if (result.success) {
      success('Checklist submitted successfully!');
      // Reset checklist
      setCheckedItems({});
      setPhotos([]);
      saveChecklistProgress(selectedType, {});
    } else {
      error(result.error || 'Failed to submit checklist');
    }
  };

  const handleResetChecklist = () => {
    setCheckedItems({});
    setPhotos([]);
    saveChecklistProgress(selectedType, {});
    success('Checklist reset!');
  };

  // Styles
  const cardStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px'
  };

  const typeButtonStyle = (isSelected) => ({
    padding: '12px 24px',
    borderRadius: '8px',
    border: isSelected ? 'none' : '1px solid var(--border)',
    background: isSelected ? 'var(--primary)' : 'var(--surface)',
    color: isSelected ? 'white' : 'var(--text-primary)',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  });

  const progressBarContainerStyle = {
    width: '100%',
    height: '12px',
    background: 'var(--background)',
    borderRadius: '6px',
    overflow: 'hidden',
    marginBottom: '24px'
  };

  const progressBarFillStyle = (percentage) => ({
    height: '100%',
    width: `${percentage}%`,
    background: percentage === 100 ? '#10b981' : 'var(--primary)',
    transition: 'width 0.3s ease'
  });

  const checklistItemStyle = (isChecked) => ({
    background: isChecked ? 'var(--background)' : 'var(--surface)',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    opacity: isChecked ? 0.7 : 1,
    marginBottom: '12px'
  });

  const checkboxStyle = (isChecked) => ({
    width: '24px',
    height: '24px',
    borderRadius: '6px',
    border: isChecked ? 'none' : '2px solid var(--border)',
    background: isChecked ? '#10b981' : 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '16px',
    flexShrink: 0,
    transition: 'all 0.3s ease'
  });

  const uploadBoxStyle = {
    width: '100%',
    padding: '40px',
    borderRadius: '8px',
    border: '2px dashed var(--border)',
    background: 'var(--background)',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
        Loading checklists...
      </div>
    );
  }

  const completionPercentage = calculateCompletion();
  const completedCount = checklistItems.filter(item => checkedItems[item]).length;

  return (
    <div>
      {/* Toast Notifications */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      {/* Checklist Type Selection */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {checklistTypes.map(type => (
          <button
            key={type}
            style={typeButtonStyle(selectedType === type)}
            onClick={() => setSelectedType(type)}
            onMouseEnter={(e) => {
              if (selectedType !== type) {
                e.currentTarget.style.background = 'var(--background)';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedType !== type) {
                e.currentTarget.style.background = 'var(--surface)';
              }
            }}
          >
            {type}
          </button>
        ))}
      </div>

      {/* Checklist Card */}
      <div style={cardStyle}>
        {/* Header with Stats */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>
              {selectedType} Checklist
            </h3>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
              {completedCount} of {checklistItems.length} completed
            </p>
          </div>
          <div style={{
            fontSize: '32px',
            fontWeight: '700',
            color: completionPercentage === 100 ? '#10b981' : 'var(--primary)'
          }}>
            {completionPercentage}%
          </div>
        </div>

        {/* Progress Bar */}
        <div style={progressBarContainerStyle}>
          <div style={progressBarFillStyle(completionPercentage)} />
        </div>

        {/* Checklist Items */}
        <div>
          {checklistItems.map((item, index) => (
            <div
              key={index}
              style={checklistItemStyle(checkedItems[item])}
              onClick={() => toggleItem(item)}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(4px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}
            >
              <div style={checkboxStyle(checkedItems[item])}>
                {checkedItems[item] && '✓'}
              </div>
              <div style={{
                flex: 1,
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                textDecoration: checkedItems[item] ? 'line-through' : 'none'
              }}>
                {item}
              </div>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                color: 'var(--text-secondary)'
              }}>
                {index + 1}/{checklistItems.length}
              </div>
            </div>
          ))}
        </div>

        {/* Photo Upload Section */}
        {completionPercentage === 100 && (
          <div style={{ marginTop: '24px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px' }}>
              Add Photos (Optional)
            </h4>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
              style={{ display: 'none' }}
              id="checklist-photo-upload"
            />
            <div
              onClick={() => document.getElementById('checklist-photo-upload').click()}
              style={uploadBoxStyle}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--primary)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
            >
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📷</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>
                Click to add photos
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                Documentation photos for completed checklist
              </div>
              {photos.length > 0 && (
                <div style={{ fontSize: '12px', color: '#10b981', marginTop: '8px', fontWeight: '600' }}>
                  {photos.length} photo(s) uploaded
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ marginTop: '24px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {completionPercentage === 100 ? (
            <>
              <Button variant="primary" onClick={handleSubmitChecklist} style={{ flex: 1 }}>
                Submit Checklist
              </Button>
              <Button variant="secondary" onClick={handleResetChecklist} style={{ flex: 1 }}>
                Reset
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={handleResetChecklist}>
              Reset Progress
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChecklistView;